// Trades on a simulated league (TRADES.md): the pure engine from @fsp/trades
// hosted by the sim. Ken proposes, answers, votes, approves, and vetoes
// through the same intents the API will take; the other managers answer
// his offers and vote on the clock by their dials; execution moves rosters
// through the same lineup rules as waivers and lands in the same log.

import { styleOf, type BotStyle } from '@fsp/bots';
import type {
  DropOption,
  TradeBlockEntry,
  TradeBuilderView,
  TradeRosterRow,
  TradesView,
  TradeView,
} from '@fsp/domain';
import { formatInZone } from '@fsp/domain';
import {
  advance,
  apply,
  botAnswer,
  botVotes,
  dropsToFit,
  isLopsided,
  OFFER_TTL_MS,
  readoutFor,
  filledLineupFor,
  spokenFor,
  tradeBuilderView as builderViewOf,
  tradeChatLine,
  tradeView as tradeViewOf,
  tradesView as tradesViewOf,
  vetoesNeeded,
  type BuilderInput,
  type Trade,
  type TradeBook,
  type TradeContext,
  type TradeEvent,
  type TradeHost,
  type TradeIntent,
  type ValueContext,
} from '@fsp/trades';
import type { Transaction } from '@fsp/waivers';
import { football } from '@fsp/sports-football';
import { systemMessage } from './chat';
import { draftContext } from './draft';
import type { SimPlayer, SimState, SimTeam } from './league';
import { hashSeed, rng } from './rng';
import { gameStatus, leagueRef, playerSummary, projection, teamRef } from './views';
import { applyMove, lockedPlayers, waiverContext } from './waivers';

const H = 3600 * 1000;

export interface BlockEntry {
  teamId: string;
  playerId: string;
  wants: string;
  at: number;
}

export interface TradeState {
  book: TradeBook;
  /** Simulated managers' pending answers: at this time, this intent. */
  planned: { at: number; intent: TradeIntent }[];
  /** Events already narrated, so a replayed tick does not post twice. */
  narrated: Set<string>;
  /** The trade block (TRADES.md): players marked available, with what the owner wants. */
  block: BlockEntry[];
}

export function emptyTradeState(): TradeState {
  return { book: { trades: [] }, planned: [], narrated: new Set(), block: [] };
}

const WANTS = [
  'a WR2 with a pulse',
  'RB depth, any flavor',
  'a tight end who catches passes',
  'a kicker and a favor',
  'youth. Picks if we ever trade them',
  'whatever gets me through the bye',
];

/** A few other managers shop a mid-roster player each week, so the block is never empty. */
function seedBlock(state: SimState): void {
  const r = rng(hashSeed(state.seed, 'block', state.currentPeriod));
  const others = state.teams.filter((t) => !t.isMine && t.ownerName);
  const shoppers = r.shuffle(others).slice(0, Math.min(3, others.length));
  const at = state.periodStart + 20 * H;
  shoppers.forEach((team, i) => {
    const skill = team.roster
      .map((id) => state.players.get(id)!)
      .filter((p) => p.positions[0] !== 'K' && p.positions[0] !== 'DST' && !p.injury)
      .sort((a, b) => b.ppg - a.ppg);
    const player = skill[4] ?? skill[skill.length - 1];
    if (!player) return;
    state.tradeState.block.push({
      teamId: team.id,
      playerId: player.id,
      wants: WANTS[r.int(0, WANTS.length - 1)]!,
      at: at + i * 3 * H,
    });
  });
}

/**
 * Put a player on the block with what the owner wants, update the note, or
 * take him off (`wants` null). Returns a reason when refused.
 */
export function setTradeBlock(
  state: SimState,
  teamId: string,
  playerId: string,
  wants: string | null,
  now: number,
): string | null {
  const team = teamOf(state, teamId);
  if (!team) return 'Unknown team.';
  const block = state.tradeState.block;
  const idx = block.findIndex((b) => b.teamId === teamId && b.playerId === playerId);
  if (wants === null) {
    if (idx < 0) return 'He was not on your block.';
    block.splice(idx, 1);
    return null;
  }
  if (!team.roster.includes(playerId)) return 'He is not on your roster.';
  if (idx >= 0) block[idx] = { ...block[idx]!, wants };
  else block.push({ teamId, playerId, wants, at: now });
  return null;
}

/** The block as the screen shows it: mine first, then everyone else's, newest first; only players still owned. */
function blockView(state: SimState, now: number): TradeBlockEntry[] {
  const mine = myTeam(state);
  return state.tradeState.block
    .filter((b) => teamOf(state, b.teamId)?.roster.includes(b.playerId))
    .map((b) => {
      const team = teamOf(state, b.teamId)!;
      const p = state.players.get(b.playerId)!;
      return {
        team: teamRef(state, team, now),
        player: playerSummary(state, p),
        projection: projection(state, p),
        wants: b.wants,
        since: iso(b.at),
        isMine: team.id === mine.id,
      };
    })
    .sort((a, b) => Number(b.isMine) - Number(a.isMine) || b.since.localeCompare(a.since));
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

const slotDefs = new Map(football.slots.map((s) => [s.id, s]));

function myTeam(state: SimState): SimTeam {
  return state.teams.find((t) => t.isMine)!;
}

function teamOf(state: SimState, id: string): SimTeam | undefined {
  return state.teams.find((t) => t.id === id);
}

// ── Context ───────────────────────────────────────────────────────────────

export function tradeContext(state: SimState, now: number): TradeContext {
  const w = waiverContext(state);
  const commissioner = state.isCommissioner
    ? myTeam(state).id
    : (state.teams.find((t) => t.ownerName === state.commissionerName)?.id ?? null);
  // WHO EACH TEAM HOLDS ON IR (D9), who count toward no limit and need no
  // seat, as the league's host says through `reservedAt`.
  const reserved = new Map(
    state.teams.map((t) => [
      t.id,
      new Set(
        t.lineup
          .filter((row) => row.playerId && slotDefs.get(row.slot)?.kind === 'reserve')
          .map((row) => row.playerId!),
      ),
    ]),
  );
  return {
    reserved: (teamId) => reserved.get(teamId) ?? new Set<string>(),
    rules: state.settings.trades,
    roster: w.roster,
    rosters: Object.fromEntries(state.teams.map((t) => [t.id, t.roster])),
    locked: lockedPlayers(state, now),
    currentPeriod: state.currentPeriod,
    inPlayoffs: state.currentPeriod > state.regularSeasonEnd,
    periodEnd: state.periodEnd,
    commissionerTeamId: commissioner,
    reviewerTeamIds: commissioner === null ? [] : [commissioner],
    teamIds: state.teams.filter((t) => t.ownerName).map((t) => t.id),
    positionOf: w.positionOf,
    playerName: w.playerName,
    teamName: w.teamName,
    offerTtlMs: OFFER_TTL_MS,
    newId: (at) => `${state.leagueId}-trade-${at}-${state.tradeState.book.trades.length}`,
    // THE SEATS A BOT PLAYS (B-481): a trade with one goes through once
    // agreed. The practice league's own managers are played as people and
    // are reviewed as its rules say.
    bots: new Set(state.teams.filter((t) => t.ai !== null).map((t) => t.id)),
  };
}

/**
 * Best legal lineup total for a roster: greedy by worth, dedicated slots
 * first, then flex. The worth the rows show, the season's points a game, so
 * the read-out's lineup line and its first sentence weigh one number (B-185).
 * Given the wire's best by position, he stands in a slot he beats
 * (`ValueContext.bestLineup`'s `filled`, B-294).
 */
function bestLineupTotal(
  state: SimState,
  roster: readonly string[],
  wire?: (position: string) => number,
): number {
  const available = new Set(roster);
  const proj = (id: string) => state.players.get(id)!.ppg;
  let total = 0;
  const starters = state.settings.roster.slots
    .map(({ slot, count }) => ({ def: slotDefs.get(slot), count }))
    .filter((s) => s.def?.kind === 'starter')
    .sort((a, b) => a.def!.eligible.length - b.def!.eligible.length);
  for (const { def, count } of starters) {
    const free = wire === undefined ? null : Math.max(0, ...def!.eligible.map(wire));
    for (let i = 0; i < count; i++) {
      const best = [...available]
        .filter((id) => def!.eligible.includes(state.players.get(id)!.positions[0]))
        .sort((a, b) => proj(b) - proj(a))[0];
      if (free !== null && (!best || proj(best) < free)) {
        total += free;
        continue;
      }
      if (!best) continue;
      available.delete(best);
      total += proj(best);
    }
  }
  return Math.round(total * 10) / 10;
}

export function valueContext(state: SimState): ValueContext {
  const dc = draftContext(state);
  const teamCount = state.teams.length;
  // Replacement level per position: the best player nobody owns, or the league-wide cutoff.
  const owned = new Set(state.teams.flatMap((t) => t.roster));
  const replacement = new Map<string, number>();
  for (const pos of football.positions) {
    const free = [...state.players.values()]
      .filter((p) => p.positions[0] === pos.id && !owned.has(p.id) && !p.injury)
      .sort((a, b) => b.ppg - a.ppg)[0];
    const cutoff = [...state.players.values()]
      .filter((p) => p.positions[0] === pos.id)
      .sort((a, b) => b.ppg - a.ppg)[(dc.starterNeeds[pos.id] ?? 0) * teamCount]?.ppg;
    replacement.set(pos.id, free?.ppg ?? cutoff ?? 0);
  }
  return {
    value: (id) => state.players.get(id)?.ppg ?? 0,
    replacement: (pos) => replacement.get(pos) ?? 0,
    bestLineup: (roster, filled) =>
      bestLineupTotal(state, roster, filled ? (pos) => replacement.get(pos) ?? 0 : undefined),
    starterNeed: (pos) => dc.starterNeeds[pos] ?? 0,
    positionOf: dc.positionOf,
    playerName: (id) => state.players.get(id)?.name ?? 'somebody',
    teamName: (id) => teamOf(state, id)?.name ?? 'a team',
    outStatus: (id) => {
      const status = state.players.get(id)?.injury?.status;
      const known = football.injuryStatuses.find((d) => d.id === status);
      return known?.severity === 'out' ? known.name : null;
    },
  };
}

// ── Intents ───────────────────────────────────────────────────────────────

/** Apply an intent, narrate its events, apply executions to rosters. */
export function tradeIntent(state: SimState, intent: TradeIntent, now: number): TradeEvent[] {
  const ctx = tradeContext(state, now);
  const r = apply(state.tradeState.book, intent, now, ctx);
  state.tradeState.book = r.book;
  settle(state, r.events, now);
  onProposed(state, r.events, now);
  return r.events;
}

/** Roster changes and chat for a batch of events; idempotent per event key. */
function settle(state: SimState, events: TradeEvent[], now: number): void {
  const ts = state.tradeState;
  for (const e of events) {
    if (e.type === 'rejected') continue;
    const key = `${e.type}:${e.trade.id}:${e.type === 'voted' ? e.teamId : ''}`;
    if (ts.narrated.has(key)) continue;
    ts.narrated.add(key);
    const at = e.type === 'executed' ? (e.trade.executeAt ?? now) : now;
    if (e.type === 'executed') {
      for (const m of e.moves) {
        const team = teamOf(state, m.teamId);
        if (!team) continue;
        // Traded-away players leave without touching waivers; extra drops go to the wire.
        for (const id of m.removed.filter((x) => !m.dropped.includes(x))) {
          team.roster = team.roster.filter((x) => x !== id);
          for (const row of team.lineup) if (row.playerId === id) row.playerId = null;
        }
        applyMove(state, team, m.added, m.dropped, at);
        const tx: Transaction = {
          id: `${e.trade.id}:${m.teamId}`,
          at,
          kind: 'trade',
          teamId: m.teamId,
          added: [...m.added],
          dropped: [...m.removed],
          faab: null,
          reason: null,
          reversedBy: null,
          claimId: null,
          runId: null,
          tradeId: e.trade.id,
        };
        state.waiverState.transactions.push(tx);
      }
    }
    const line = tradeChatLine(e, {
      ownerName: (id) => teamOf(state, id)?.ownerName ?? 'Someone',
      playerName: (id) => state.players.get(id)?.name ?? 'somebody',
      commissionerName: state.commissionerName,
      leagueZone: state.settings.identity.timeZone,
      review: state.settings.trades.review,
    });
    if (line !== null) state.chat.push(systemMessage(state, at, line));
    if (e.type === 'review.started') planVotes(state, e.trade, now);
  }
}

/**
 * Move trades forward to `now`: simulated managers answer and vote when
 * their time comes, offers expire, review windows end, cleared trades
 * execute. Returns true when anything changed.
 */
export function advanceTrades(state: SimState, now: number): boolean {
  if (state.status !== 'in-season') return false;
  const ts = state.tradeState;
  let changed = false;
  const due = ts.planned.filter((p) => p.at <= now).sort((a, b) => a.at - b.at);
  if (due.length) {
    ts.planned = ts.planned.filter((p) => p.at > now);
    for (const p of due) {
      const r = apply(ts.book, p.intent, p.at, tradeContext(state, p.at));
      ts.book = r.book;
      settle(state, r.events, p.at);
    }
    changed = true;
  }
  const r = advance(ts.book, now, tradeContext(state, now));
  if (r.events.length) {
    ts.book = r.book;
    settle(state, r.events, now);
    changed = true;
  }
  return changed;
}

// ── Other managers ────────────────────────────────────────────────────────

/**
 * How a team answers a trade (A4): an AI seat by its character's style, a
 * simulated person by a style of their own — a skill drawn by seed between
 * Casual and Sharp, every dial in the middle.
 */
function styleFor(state: SimState, team: SimTeam): BotStyle {
  if (team.ai) return styleOf(team.ai);
  const skill = 20 + rng(hashSeed(state.seed, 'sharp', team.id)).next() * 60;
  return styleOf({
    skill,
    dials: { trashTalk: 0.5, aggression: 0.5, risk: 0.5, loyalty: 0.5, activity: 0.5 },
  });
}

/**
 * The players a team holds that a trade may move: none on IR, whose drop
 * makes no room and who is not offered, and none a trade of `teamId`'s
 * names — the answering bot's (B-293), as the league's host holds it.
 */
function movable(state: SimState, team: SimTeam, teamId: string): string[] {
  const reserve = new Set(
    team.lineup
      .filter((row) => row.playerId && slotDefs.get(row.slot)?.kind === 'reserve')
      .map((row) => row.playerId!),
  );
  const spoken = spokenFor(state.tradeState.book.trades, teamId);
  return team.roster.filter((id) => !reserve.has(id) && !spoken.has(id));
}

/**
 * A team's players a counter may ask for, each with his points a game over
 * the best free agent at his position — whatever slot he sits in (DIRECTION
 * A19): a starter is as much a trade piece as a bench player.
 */
function piecesOf(
  state: SimState,
  team: SimTeam,
  answering: string,
): { id: string; over: number }[] {
  const value = valueContext(state);
  return movable(state, team, answering).map((id) => ({
    id,
    over: Math.max(0, value.value(id) - value.replacement(value.positionOf(id))),
  }));
}

/**
 * Plan a simulated manager's answer to an offer just made to them
 * (`botAnswer` in `@fsp/trades`, which a real league's bots answer by too).
 * Deterministic by seed and trade id.
 */
export function planAnswer(state: SimState, trade: Trade, now: number): void {
  const [from, to] = trade.sides;
  const team = teamOf(state, to.teamId);
  if (!team || team.isMine) return;
  const proposer = teamOf(state, from.teamId);
  const style = styleFor(state, team);
  const rand = rng(hashSeed(state.seed, 'answer', trade.id));
  const answer = botAnswer({
    trade,
    readout: readoutFor(simHost(state, now), trade, to.teamId),
    filled: filledLineupFor(simHost(state, now), trade, to.teamId),
    bar: style.answerBar,
    haggle: style.haggle,
    draftPremium: style.draftPremium,
    drafted: (id) =>
      state.draft?.state.picks.some((p) => p.teamId === team.id && p.playerId === id) ?? false,
    rand: () => rand.next(),
    proposerPieces: proposer ? piecesOf(state, proposer, team.id) : [],
    maxPerSide: state.settings.trades.maxPlayersPerSide,
    dropsFor: (teamId, incoming, outgoing) => {
      const t = teamOf(state, teamId);
      return t ? dropsFor(state, t, incoming, outgoing, now) : [];
    },
    playerName: (id) => state.players.get(id)?.name ?? 'somebody',
    isOut: (id) => valueContext(state).outStatus?.(id) != null,
  });
  ts(state).planned.push({ at: now + answer.delayMs, intent: answer.intent });
}

function ts(state: SimState): TradeState {
  return state.tradeState;
}

/**
 * The fewest of a team's lowest-scoring players that make its roster legal
 * after receiving `incoming` and giving `outgoing` — by points, never by
 * slot (DIRECTION A19), and never so that somebody is left with no seat
 * (`dropsToFit`, B-480).
 */
function dropsFor(
  state: SimState,
  team: SimTeam,
  incoming: readonly string[],
  outgoing: readonly string[],
  now: number,
): string[] {
  return dropsToFit(
    tradeContext(state, now),
    team.id,
    [...team.roster.filter((id) => !outgoing.includes(id)), ...incoming],
    movable(state, team, team.id)
      .filter((id) => !outgoing.includes(id))
      .map((id) => state.players.get(id)!)
      .sort((a, b) => a.ppg - b.ppg)
      .map((p) => p.id),
  );
}

/** In a league vote, other managers vote over the window (`botVotes`). */
function planVotes(state: SimState, trade: Trade, now: number): void {
  if (state.settings.trades.review !== 'league-vote') return;
  const [a, b] = trade.sides;
  const votes = botVotes({
    trade,
    lopsided: isLopsided(readoutFor(simHost(state, now), trade, a.teamId)),
    needed: vetoesNeeded(tradeContext(state, now), trade),
    windowHours: state.settings.trades.reviewWindowHours,
    voters: state.teams
      .filter((t) => !t.isMine && t.ownerName && t.id !== a.teamId && t.id !== b.teamId)
      .map((t) => {
        const rand = rng(hashSeed(state.seed, 'vote', trade.id, t.id));
        return { teamId: t.id, isBot: Boolean(t.ai), rand: () => rand.next() };
      }),
  });
  for (const v of votes)
    ts(state).planned.push({
      at: now + v.delayMs,
      intent: { type: 'vote', tradeId: trade.id, by: v.teamId, veto: v.veto },
    });
}

/** Ken's League opens with an offer from Dave waiting on Home (HOME.md). */
export function initTrades(state: SimState): void {
  if (state.status !== 'in-season') return;
  seedBlock(state);
  const mine = myTeam(state);
  const dave = state.teams.find((t) => !t.isMine && t.ownerName);
  if (!dave) return;
  // Dave offers his fourth-best skill player for my second-best: close enough to think about.
  const skill = (t: SimTeam) =>
    t.roster
      .map((id) => state.players.get(id)!)
      .filter((p) => p.positions[0] !== 'K' && p.positions[0] !== 'DST' && !p.injury)
      .sort((a, b) => b.ppg - a.ppg);
  const give = skill(dave)[3];
  const get = skill(mine)[1];
  if (!give || !get) return;
  const at = state.sundayUtcMidnight - 10 * H; // Saturday evening
  const r = apply(
    state.tradeState.book,
    {
      type: 'propose',
      by: dave.id,
      to: mine.id,
      give: [give.id],
      get: [get.id],
      myDrops: [],
      note: `${get.shortName} would fix my bye week. ${give.shortName} is better than his numbers.`,
      counterOf: null,
    },
    at,
    tradeContext(state, at),
  );
  state.tradeState.book = r.book;
  settle(state, r.events, at);
}

/** Every proposal Ken makes gets an answer planned for the other manager. */
export function onProposed(state: SimState, events: TradeEvent[], now: number): void {
  for (const e of events)
    if (
      (e.type === 'proposed' || e.type === 'countered') &&
      !teamOf(state, e.trade.sides[1].teamId)?.isMine
    )
      planAnswer(state, e.trade, now);
}

// ── Views ─────────────────────────────────────────────────────────────────

function rosterRow(state: SimState, team: SimTeam, id: string, now: number): TradeRosterRow {
  const p = state.players.get(id)!;
  const row = team.lineup.find((r) => r.playerId === id);
  return {
    player: playerSummary(state, p),
    projection: projection(state, p),
    // The tour's worth is its value: the season's points a game (`valueContext`).
    worth: Math.round(p.ppg * 10) / 10,
    // The tour plays one week: no season to add up.
    seasonAvg: null,
    seasonPoints: null,
    game: gameStatus(state, p, now),
    locked: lockedPlayers(state, now).has(id),
    slot: row?.slot ?? 'BN',
    starting: row ? slotDefs.get(row.slot)?.kind === 'starter' : false,
  };
}

/** The shared views' host over the simulator's state (`TradeHost` in `@fsp/trades`). */
function simHost(state: SimState, now: number): TradeHost {
  return {
    now,
    myTeamId: myTeam(state).id,
    book: state.tradeState.book,
    ctx: tradeContext(state, now),
    value: valueContext(state),
    rosterOf: (id) => teamOf(state, id)?.roster ?? [],
    teamRef: (id) => teamRef(state, teamOf(state, id)!, now),
    ownerName: (id) => teamOf(state, id)?.ownerName ?? '',
    row: (teamId, playerId) => rosterRow(state, teamOf(state, teamId)!, playerId, now),
    summary: (id) => playerSummary(state, state.players.get(id)!),
    when: (ms) => when(ms, state),
    commissionerName: state.commissionerName,
    // The practice league is the reader's alone, and plays no elimination format.
    castsVote: true,
    myTeamOut: false,
    outTeamIds: new Set(),
    leagueClosed: state.status === 'in-season' ? null : 'Trades open once the league has drafted.',
  };
}

/** A trade's time on its view, in the reader's zone and named (B-181). */
function when(ms: number, state: SimState): string {
  return formatInZone(ms, state.readerZone ?? state.settings.identity.timeZone, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function tradeView(state: SimState, trade: Trade, now: number): TradeView {
  return tradeViewOf(simHost(state, now), trade);
}

export function tradesView(state: SimState, now: number): TradesView {
  return tradesViewOf(simHost(state, now), {
    league: leagueRef(state),
    block: blockView(state, now),
  });
}

export type { BuilderInput };

export function tradeBuilderView(
  state: SimState,
  input: BuilderInput,
  now: number,
): TradeBuilderView {
  const mine = myTeam(state);
  const locked = lockedPlayers(state, now);
  const dropOption = (id: string, droppable: boolean): DropOption => {
    const p = state.players.get(id)!;
    const row = mine.lineup.find((r) => r.playerId === id);
    return {
      player: playerSummary(state, p),
      game: gameStatus(state, p, now),
      projection: projection(state, p),
      slot: row?.slot ?? 'BN',
      starting: row ? slotDefs.get(row.slot)?.kind === 'starter' : false,
      locked: locked.has(id),
      consequence: null,
      droppable,
      // A trade's drops happen when it executes, an instant not known while it is built.
      dropsTo: null,
    };
  };
  return builderViewOf(simHost(state, now), input, dropOption);
}

/** Home's "Dave wants to trade" card, from real offers. */
export function offersForMe(state: SimState, now: number): TradeView[] {
  if (state.status !== 'in-season') return [];
  return tradesView(state, now).forMe;
}

export type { SimPlayer };
